import React, { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";
import api from "@/lib/api";

const PLACEHOLDERS = {
  summary: "e.g. Data analyst with 3 years experience in ETL and dashboards, looking for a Salesforce Data Cloud role...",
  skills: "e.g. I work with SOQL, Python, Data Cloud, and Tableau daily...",
  education: "e.g. B.Tech in Computer Science from XYZ University, 2018 to 2022...",
  experience: "e.g. Worked at TCS as a Data Analyst for 3 years, built ETL pipelines and dashboards...",
  projects: "e.g. Built a Data Cloud simulator using React and FastAPI for a hackathon...",
  internships: "e.g. Interned at Acme Corp as a Salesforce intern for 6 months in 2024...",
  certifications: "e.g. Salesforce Administrator certification, obtained in 2024...",
  languages: "e.g. Native Hindi speaker, fluent in English...",
  websites: "e.g. My LinkedIn is linkedin.com/in/johndoe...",
  hobbies: "e.g. I enjoy chess, reading sci-fi novels, and hiking...",
  extracurricular: "e.g. I volunteer at a local NGO and run a college tech club...",
  custom: "e.g. Won Best Intern Award 2024 at TCS, published a paper on Data Cloud architecture...",
};

/** Applies the AI-generated result for a given section into the draft state — single-entry
 * sections get a new array item appended, list sections get their items merged in (deduped). */
function applyResult(section, data, draft, setDraft) {
  const mergeList = (field) => {
    const merged = [...(draft[field] || [])];
    (data.items || []).forEach((s) => { if (s && !merged.includes(s)) merged.push(s); });
    setDraft({ ...draft, [field]: merged });
  };
  switch (section) {
    case "summary":
      setDraft({ ...draft, summary: data.summary || draft.summary });
      break;
    case "skills": {
      const merged = [...draft.skills];
      (data.skills || []).forEach((s) => { if (s && !merged.includes(s)) merged.push(s); });
      setDraft({ ...draft, skills: merged });
      break;
    }
    case "hobbies": mergeList("hobbies"); break;
    case "extracurricular": mergeList("extracurricular"); break;
    case "custom":
      setDraft({ ...draft, custom_sections: [...draft.custom_sections, { id: `${Date.now()}`, title: data.title || "", items: data.items || [] }] });
      break;
    default:
      setDraft({ ...draft, [section]: [...(draft[section] || []), data] });
  }
}

export default function AiPromptGenerator({ section, draft, setDraft }) {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [generating, setGenerating] = useState(false);

  const generate = async () => {
    if (prompt.trim().length < 3) {
      toast.error("Describe it a bit more first");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post("/resume-builder/generate-from-prompt", { section, prompt, target_role: draft.target_role });
      applyResult(section, data, draft, setDraft);
      toast.success("Added — review & edit as needed");
      setPrompt("");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate right now");
    } finally {
      setGenerating(false);
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid={`resume-ai-prompt-toggle-${section}`} className="flex items-center gap-1.5 text-xs font-semibold text-indigo-300 hover:text-indigo-200">
        <Sparkles className="w-3.5 h-3.5" /> Generate with AI
      </button>
    );
  }

  return (
    <div className="mt-1 bg-white/[0.03] rounded-lg p-3" data-testid={`resume-ai-prompt-box-${section}`}>
      <textarea
        value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2}
        placeholder={PLACEHOLDERS[section] || "Describe it in your own words..."}
        data-testid={`resume-ai-prompt-input-${section}`}
        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-400 resize-none"
      />
      <div className="flex items-center gap-2 mt-2">
        <button
          onClick={generate} disabled={generating} data-testid={`resume-ai-prompt-generate-${section}`}
          className="flex items-center gap-1.5 bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
        >
          {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Generate
        </button>
        <button onClick={() => { setOpen(false); setPrompt(""); }} data-testid={`resume-ai-prompt-cancel-${section}`} className="text-xs text-slate-400 hover:text-white">Cancel</button>
      </div>
    </div>
  );
}
